"use client";

import { useEffect, useState } from "react";
import { AlertTriangle, Copy, RefreshCw, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { clarityEvent, claritySetTag, clarityUpgrade, trackEvent } from "@/lib/analytics";
import {
  classifyRuntimeError,
  normalizeRuntimeErrorMessage,
  reloadOnceForChunkError,
  runtimeErrorEventName,
  shouldAutoReloadForChunkError,
} from "@/lib/runtime-error-monitor";
import { reportClientError } from "@/lib/error-report";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: ErrorProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    console.error("[GlobalError]", error);
    const kind = classifyRuntimeError(error, "client_render");
    claritySetTag("runtime_error_kind", kind);
    claritySetTag("runtime_error_message", normalizeRuntimeErrorMessage(error));
    claritySetTag("runtime_error_path", window.location.pathname);
    clarityEvent(runtimeErrorEventName(kind));
    clarityUpgrade(runtimeErrorEventName(kind));
    trackEvent("runtime_error", {
      error_kind: kind,
      error_message: normalizeRuntimeErrorMessage(error),
    });
    reportClientError({
      message: error.message,
      digest: error.digest,
      kind: "react_error",
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      stack: error.stack,
    });
    if (shouldAutoReloadForChunkError(error)) {
      reloadOnceForChunkError();
    }
  }, [error]);

  const errorInfo = [
    `时间: ${new Date().toLocaleString("zh-CN")}`,
    `页面: ${typeof window !== "undefined" ? window.location.href : "unknown"}`,
    `UA: ${typeof navigator !== "undefined" ? navigator.userAgent : "unknown"}`,
    `错误: ${error.message || "未知错误"}`,
    error.digest ? `Digest: ${error.digest}` : "",
    error.stack ? `堆栈:\n${error.stack}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(errorInfo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = errorInfo;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
          <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-foreground">页面出了点问题</h2>
          <p className="text-sm text-muted-foreground">
            很抱歉，页面遇到了意外错误。你可以尝试刷新，或加入 QQ 群反馈问题。
          </p>
        </div>

        <div className="space-y-3">
          <Button onClick={reset} className="w-full gap-2">
            <RefreshCw className="h-4 w-4" />
            重新加载
          </Button>

          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => window.open("https://qm.qq.com/q/IV2d8UfGkO", "_blank")}
          >
            <MessageCircle className="h-4 w-4" />
            加入 QQ 群反馈
          </Button>

          <Button variant="ghost" className="w-full gap-2" onClick={handleCopy}>
            <Copy className="h-4 w-4" />
            {copied ? "已复制" : "复制错误信息"}
          </Button>
        </div>

        <details className="text-left">
          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
            查看错误详情
          </summary>
          <pre className="mt-2 max-h-48 overflow-auto rounded-lg border bg-muted p-3 text-xs text-muted-foreground whitespace-pre-wrap break-all">
            {errorInfo}
          </pre>
        </details>
      </div>
    </div>
  );
}
